import { ArrowLeft, Check, Pencil, Trash2, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useEmployee } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { useMembers } from '@/features/members';
import { useSession } from '@/stores/session';
import { LEAVE_STATUS_LABEL, LEAVE_TYPE_LABEL, type LeaveStatus } from '../constants';
import {
  useApproveTimeOff,
  useDeleteTimeOff,
  useRejectTimeOff,
  useTimeOffItem,
} from '../queries';

const STATUS_VARIANT: Record<LeaveStatus, 'secondary' | 'outline' | 'destructive'> = {
  requested: 'outline',
  approved: 'secondary',
  rejected: 'destructive',
  cancelled: 'outline',
};

const HR_ROLES = new Set(['founder', 'super_admin', 'hr']);

function fmtDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function TimeOffDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const role = useSession((s) => s.role);
  const canApprove = role ? HR_ROLES.has(role) : false;

  const { data: request, isPending, isError, error } = useTimeOffItem(publicId);
  const employee = useEmployee(request?.employee ?? -1);
  const { data: membersData } = useMembers();
  const del = useDeleteTimeOff();
  const approve = useApproveTimeOff();
  const reject = useRejectTimeOff();

  async function handleDelete() {
    if (!request) return;
    await del.mutateAsync(request.public_id);
    navigate('/hrm/time-off');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/time-off')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const employeeLabel = employee.data ? employeeName(employee.data) : `#${request.employee}`;
  const approverLabel = request.approver
    ? (membersData?.results.find((m) => m.user.id === request.approver)?.user.full_name ?? '…')
    : '—';
  const isPendingDecision = request.status === 'requested';

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/hrm/time-off')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{employeeLabel}</h1>
          <Badge variant={STATUS_VARIANT[request.status]}>
            {LEAVE_STATUS_LABEL[request.status]}
          </Badge>
        </div>
        <div className="flex gap-2">
          {canApprove && isPendingDecision ? (
            <>
              <Button
                variant="outline"
                onClick={() => approve.mutate(request.public_id)}
                disabled={approve.isPending}
              >
                <Check /> Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => reject.mutate(request.public_id)}
                disabled={reject.isPending}
              >
                <X /> Reject
              </Button>
            </>
          ) : null}
          <Button
            variant="outline"
            onClick={() => navigate(`/hrm/time-off/${request.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete request?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Request</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Employee">{employeeLabel}</Field>
          <Field label="Type">{LEAVE_TYPE_LABEL[request.type]}</Field>
          <Field label="Days">{request.days}</Field>
          <Field label="Start">{fmtDate(request.start_date)}</Field>
          <Field label="End">{fmtDate(request.end_date)}</Field>
          <Field label="Approver">{approverLabel}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="break-words whitespace-pre-wrap">{request.reason || '—'}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
